import DOMPurify from 'dompurify';
import $ from 'jquery';
import { marked } from 'marked';

// https://github.com/markedjs/marked/issues/1232 - sanitizer use
// https://github.com/cure53/DOMPurify/issues/317 - target blank

DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
	if (node instanceof HTMLAnchorElement) {
		node.setAttribute('target', '_blank');
		node.setAttribute('rel', 'noopener');
	}
});

function centerElements(
	el: JQuery<HTMLElement>,
	options?: { centerOn?: JQuery<HTMLElement>; adjustX?: number; adjustY?: number },
): void {
	const defaultOptions = {
		adjustX: 0,
		adjustY: 0,
	};

	options = $.extend({}, defaultOptions, options);

	el.each(function () {
		const doc = document,
			t = $(this);
		let left = '50%',
			top;

		if (options?.centerOn) {
			// we center it not by window but by some element
			left =
				options.centerOn.position().left + (options.centerOn.outerWidth() || 0) / 2 + 'px';
			top =
				options.centerOn.position().top +
				((options.centerOn.outerHeight() || 0) - t.outerHeight()!) / 2;
		} else {
			top = Math.max(
				Math.floor($(doc.body).outerHeight()! - t.outerHeight()! / 2) + $(doc).scrollTop()!,
				0,
			);
		}

		t.css({
			top: top + (options?.adjustY || 0) + 'px',
			left: left,
			marginLeft: '-' + (Math.floor(t.outerWidth()! / 2) + (options?.adjustX || 0)) + 'px',
		});
	});
}

function scaleValToAutofitJQ(
	ct: JQuery<HTMLElement>,
	minSizeEm: number,
	maxSizeEm: number,
	blockCt?: JQuery<HTMLElement>,
): void {
	ct.each(function () {
		const el = $(this),
			valCt = el.find('.val'),
			stepEm = 0.1, //em
			getMaxHeight = function (jqObj: JQuery<HTMLElement>) {
				const heights: number[] = [];
				jqObj.each((_k, e) => {
					const h = $(e).height();
					if (typeof h === 'number') {
						heights.push(h);
					}
				});
				return Math.max.apply(null, heights);
			};
		let maxHeightPx = Infinity,
			currentSizeEm = minSizeEm, // minimum size that we support
			prevSizeEm = currentSizeEm,
			heightPx,
			prevHeightPx;

		if (blockCt) {
			// specify for blocks that have maximum height (nonscrollable blocks on datawalls)
			maxHeightPx = blockCt.height()!;
		}

		el.css('fontSize', currentSizeEm + 'em');
		heightPx = prevHeightPx = getMaxHeight(valCt);

		// up the size til we hit the max or text drops in two lines
		while (
			heightPx < 1.8 * prevHeightPx &&
			el.height()! < maxHeightPx &&
			currentSizeEm <= maxSizeEm
		) {
			prevSizeEm = currentSizeEm;
			currentSizeEm += stepEm;
			el.css('fontSize', currentSizeEm + 'em');

			prevHeightPx = heightPx;
			heightPx = getMaxHeight(valCt);
		}
		el.css('fontSize', prevSizeEm + 'em');
	});
}

function scaleValToAutofit(node: HTMLElement, minSizeEm: number, maxSizeEm: number) {
	const stepEm = 0.1;

	let currentSizeEm = minSizeEm,
		prevSizeEm = currentSizeEm;

	node.style.display = 'inline'; // so it can break in two lines
	node.style.fontSize = `${currentSizeEm}em`;
	let lines = node.getClientRects().length;

	while (lines < 2 && currentSizeEm <= maxSizeEm) {
		prevSizeEm = currentSizeEm;
		currentSizeEm += stepEm;
		node.style.fontSize = `${currentSizeEm}em`;
		lines = node.getClientRects().length;
	}

	node.style.display = 'inline-block'; // helps against creating unnecessary scrollbars
	node.style.fontSize = `${prevSizeEm}em`;
}

function scrollGuard(ct: JQuery<HTMLElement>): void {
	ct.each(function () {
		const el = $(this);
		el.off('wheel.scrollGuard').on('wheel.scrollGuard', (e: JQuery.TriggeredEvent) => {
			const elScrollTop = el.scrollTop()!;

			if ((e.originalEvent as WheelEvent).deltaY < 0) {
				/* scrolling up */
				return elScrollTop > 0;
			} else {
				/* scrolling down */
				return elScrollTop + el.innerHeight()! < (el[0]?.scrollHeight || 0);
			}
		});
	});
}

// @containerEl - mostly to check if context popup is attached to element - @el,
// that is visible in modal and screen. If second parameter is not defined,
// we checking element in the window.
interface Coordinates {
	top: number;
	left: number;
	right?: number;
	bottom?: number;
}
function isElementOnScreen(
	node: HTMLElement,
	containerNode: HTMLElement | typeof window = window,
	lookForwardX = 0,
	lookForwardY = 0,
	includeScrolledPast = false,
	lookBackwardX = 0,
	lookBackwardY = 0,
): boolean {
	if (!node) {
		return true;
	}

	let viewport: Coordinates,
		containerWidth = 0,
		containerHeight = 0;

	if (!containerNode || containerNode === window) {
		viewport = {
			top: window.scrollY || 0,
			left: window.scrollX || 0,
		};
		containerWidth = window.innerWidth;
		containerHeight = window.innerHeight;
	} else {
		const containerNodeNotWindow = containerNode as HTMLElement;
		const containerRect = containerNodeNotWindow.getBoundingClientRect();
		viewport = {
			top: containerRect.top + window.pageYOffset,
			left: containerRect.left + window.pageXOffset,
		};
		containerWidth = containerNodeNotWindow.offsetWidth;
		containerHeight = containerNodeNotWindow.offsetHeight;
	}

	viewport.right = viewport.left + containerWidth;
	viewport.bottom = viewport.top + containerHeight;

	const nodeRect = node.getBoundingClientRect();
	const bounds: Coordinates = {
		top: nodeRect.top + window.pageYOffset,
		left: nodeRect.left + window.pageXOffset,
	};

	bounds.right = bounds.left + node.offsetWidth;
	bounds.bottom = bounds.top + node.offsetHeight;

	// Apply forward look-ahead adjustments
	if (lookForwardX) {
		bounds.left -= lookForwardX;
	}
	if (lookForwardY) {
		bounds.top -= lookForwardY;
	}

	// Apply backward look-ahead adjustments
	if (lookBackwardX) {
		bounds.right += lookBackwardX;
	}
	if (lookBackwardY) {
		bounds.bottom += lookBackwardY;
	}

	let isOutsideScreen;

	const isOutsideForward = viewport.right < bounds.left || viewport.bottom < bounds.top;
	const isOutsideBackward = viewport.left > bounds.right || viewport.top > bounds.bottom;

	if (includeScrolledPast) {
		isOutsideScreen = isOutsideForward;
	} else {
		isOutsideScreen = isOutsideForward || isOutsideBackward;
	}

	return !isOutsideScreen;
}

function scaleTextToAutoFit(nodeToScale: HTMLElement, maxHeightPx: number) {
	// unused for now

	if (!nodeToScale) {
		return;
	}

	const blockCt = nodeToScale.closest<HTMLElement>('.block')!;
	let overflow;

	if (blockCt) {
		overflow = getComputedStyle(blockCt).overflowY;
		blockCt.style.overflowY = 'visible';
	}

	const minSizeEm = 1,
		maxSizeEm = 2.5,
		stepEm = 0.05;

	let heightPx,
		prevSizeEm = minSizeEm,
		currentSizeEm = minSizeEm; // minimum size that we support

	nodeToScale.style.fontSize = `${currentSizeEm}em`;
	heightPx = nodeToScale.offsetHeight;

	// up the size til we hit the max
	while (heightPx < maxHeightPx && currentSizeEm <= maxSizeEm) {
		prevSizeEm = currentSizeEm;
		currentSizeEm += stepEm;
		nodeToScale.style.fontSize = `${currentSizeEm}em`;
		heightPx = nodeToScale.offsetHeight;
	}
	nodeToScale.style.fontSize = `${prevSizeEm}em`;

	if (blockCt && overflow) {
		blockCt.style.overflowY = '';
	}
}

function nl2br(text: string): string {
	return (text + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br />$2');
}

function br2nl(text: string): string {
	return (text + '').replace(/<\s*\/?br\s*[/]?>/gi, '\n');
}

function replaceTextLinksWithHtml(text: string, linkColor?: string): string {
	//only URLs starting with http://, https://, or ftp://
	const pattern = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim,
		linkStyle = linkColor ? 'color:' + linkColor + ' !important;' : '';
	return text.replace(
		pattern,
		'<a href="$1" target="_blank" class="replacedlink" style="' + linkStyle + '">$1</a>',
	);
}

/**
 *
 *	@param {object} options
 *	@param {string} options.text Text to parse (required)
 *	@param {string=} options.linkColor Apply color to link (optional)
 *	@param {boolean=} options.hideHelpArticles Used when user wants to hide help article links, especially when WL is enabled (optional)
 *
 */
function parseMarkdownToHtml(options: {
	text: string;
	linkColor?: string;
	hideHelpArticles?: boolean;
}): string {
	if (!options.text) {
		return '';
	}

	const linkStyle = options.linkColor ? `style="color: ${options.linkColor} !important;"` : '';

	// https://marked.js.org/using_pro#renderer - renderer use
	const renderer = {
		link: (href: string, title?: string | null, text = '') => {
			const isHelpArticle = href.includes('help.databox.com'); // check if link is really help article and needs to be hidden for WL
			let link = '',
				titleAttr = '';

			if (!options.hideHelpArticles || !isHelpArticle) {
				titleAttr = title ? `title="${title}"` : '';
				link = `<a href="${href}" ${titleAttr} target="_blank" class="replacedlink" ${linkStyle}>${text}</a>`;
			}

			return link;
		},
	};

	marked.use({
		renderer,
		breaks: true,
	});

	const html = marked(options.text);

	return DOMPurify.sanitize(html);
}

function getWidescreenSizeThatFits(
	maxWidth: number,
	maxHeight: number,
	extrasHeight: number,
): { width: number; height: number } {
	const getHeightForWidth = (width: number) => {
		return (width / 16) * 9;
	};
	const getWidthForHeight = (height: number) => {
		return (height * 16) / 9;
	};

	let width = maxWidth,
		height = getHeightForWidth(width);

	if (height + extrasHeight > maxHeight) {
		height = maxHeight - extrasHeight;
		width = getWidthForHeight(height);
	}

	return { width: Math.floor(width), height: Math.floor(height) };
}

function getMaxSizeForRatio(
	ct: JQuery<HTMLElement> | JQuery<Window>,
	ratio: number,
): { width: number; height: number } {
	const winWidth = ct.width()!,
		winHeight = ct.height()!;
	let width, height;

	width = 0.9 * winWidth;
	height = width / 1.74;

	if (height > 0.9 * winHeight) {
		height = 0.9 * winHeight;
		width = ratio * height;
	}

	return {
		width,
		height,
	};
}

function sanitize(dirtyHtmlString: string, extraOptions = {}): string {
	// outputs clean and safe html
	const options = Object.assign(
		{
			USE_PROFILES: { html: true },
			ADD_TAGS: ['ui-icon'],
			ADD_ATTR: ['name', 'size', 'fill'],
		},
		extraOptions,
	);
	return DOMPurify.sanitize(dirtyHtmlString, options);
}

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function stripHtml(html: string | undefined): string {
	if (!html) return '';

	const cleanHtml = sanitize(html);

	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = cleanHtml;
	return tempDiv.textContent || tempDiv.innerText || '';
}

function isElementVisible(node: HTMLElement): boolean {
	return node && node.offsetWidth > 0 && node.offsetHeight > 0;
}

function getPreciseWidth(node: HTMLElement) {
	// returns float. offsetWidth returns integer that can be less
	return node ? parseFloat(window.getComputedStyle(node).width) || 0 : 0;
}

function getPreciseHeight(node: HTMLElement) {
	// returns float. offsetHeight returns integer that can be less
	return node ? parseFloat(window.getComputedStyle(node).height) || 0 : 0;
}

function getWidthWithMargins(node?: HTMLElement | null): number {
	if (!node) {
		return 0;
	}

	// uses & returns integer bcos we want it rounded up
	const width = getPreciseWidth(node),
		style = window.getComputedStyle(node);

	type styleProp = 'marginLeft' | 'marginRight';

	return ['marginLeft', 'marginRight']
		.map((side) => parseFloat(style[side as styleProp]))
		.reduce((total, side) => total + side, width);
}

function getHeightWithMargins(node?: HTMLElement | null): number {
	if (!node) {
		return 0;
	}

	// uses & returns integer bcos we want it rounded up
	const height = getPreciseHeight(node),
		style = window.getComputedStyle(node);

	type styleProp = 'marginTop' | 'marginBottom';

	return ['marginTop', 'marginBottom']
		.map((side) => parseFloat(style[side as styleProp]))
		.reduce((total, side) => total + side, height);
}

function getHeightWithoutPaddings(node?: HTMLElement | null): number {
	if (!node) {
		return 0;
	}

	// uses floats bcos we want it rounded down
	const style = window.getComputedStyle(node),
		height = parseFloat(style.height);

	type styleProp = 'paddingTop' | 'paddingBottom';

	const float = ['paddingTop', 'paddingBottom']
		.map((side) => parseFloat(style[side as styleProp]))
		.reduce((total, side) => total - side, height);

	return float;
}

function getWidthWithoutPaddings(node?: HTMLElement | null): number {
	if (!node) {
		return 0;
	}

	// uses floats bcos we want it rounded down
	const style = window.getComputedStyle(node),
		width = parseFloat(style.width);

	type styleProp = 'paddingLeft' | 'paddingRight';

	const float = ['paddingLeft', 'paddingRight']
		.map((side) => parseFloat(style[side as styleProp]))
		.reduce((total, side) => total - side, width);

	return float;
}

function getChildrenHeightSum(node?: HTMLElement | null): number {
	if (!node) {
		return 0;
	}

	// for simplicity, children's margins are ignored
	let height = 0;
	(Array.from(node.children) as HTMLElement[]).forEach((n) => {
		height += n.offsetHeight;
	});

	return height;
}

function stripAllHtml(html: string) {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || '';
}

export interface DuplicateCleanupConfig {
	sectionSelectors: string[]; // CSS selectors for the different sections (e.g. ['.favorites-section', '.non-favorites-section'])
	itemSelector: string; // CSS selector for items within sections (e.g. 'li.filter-sort-item[data-id]')
	shouldKeepInSection: (itemId: string, sectionSelector: string) => boolean; // Function to determine if an item should stay in a given section
}

export type NodeValidityCallback = (itemId: string, sectionSelector: string) => boolean;

// we use this to remove duplicate DOM items
// we had an issue where items were not being removed properly after being moved to a different section
// favoriting/unfavoriting item that was just manually sorted caused it to appear in both sections -> more info in task below
// ISSUE IT SOLVES: https://app.asana.com/1/7582066485656/project/1208538739128053/task/1210842952326737
function cleanupDuplicateNodes(config: DuplicateCleanupConfig): void {
	// find all items in all configured sections
	const allItems: JQuery<HTMLElement>[] = [];
	const sectionElements: JQuery<HTMLElement>[] = [];

	config.sectionSelectors.forEach((sectionSelector) => {
		const sectionElement = $(sectionSelector);
		if (sectionElement.length > 0) {
			sectionElements.push(sectionElement);
			const itemsInSection = sectionElement.find(config.itemSelector);
			allItems.push(itemsInSection);
		}
	});

	if (sectionElements.length === 0) {
		return;
	}

	// combine all items from all sections
	let combinedItems = allItems[0]!;
	for (let i = 1; i < allItems.length; i++) {
		combinedItems = combinedItems.add(allItems[i]!);
	}

	// group items by their unique ID
	const itemsByUniqueId = new Map<string, JQuery<HTMLElement>[]>();

	combinedItems.each((_index, element) => {
		const $element = $(element);
		const itemId = $element.attr('data-id');
		if (itemId) {
			if (!itemsByUniqueId.has(itemId)) {
				itemsByUniqueId.set(itemId, []);
			}
			itemsByUniqueId.get(itemId)!.push($element);
		}
	});

	// for each unique ID, check if there are duplicates and remove inappropriate ones
	itemsByUniqueId.forEach((elements, itemId) => {
		if (elements.length > 1) {
			elements.forEach(($element) => {
				// find which section this element belongs to
				let elementSection: string | undefined;
				config.sectionSelectors.forEach((sectionSelector) => {
					if ($element.closest(sectionSelector).length > 0) {
						elementSection = sectionSelector;
					}
				});

				// remove element if it shouldn't be in this section
				if (elementSection && !config.shouldKeepInSection(itemId, elementSection)) {
					$element.remove();
				}
			});
		}
	});
}

/*
 * calculates the index of a sortable item relative only to other sortable items,
 * excluding notifications and placeholders
 */
function getSortableItemIndex(item: JQuery, itemSelectorString: string): number {
	const allSortableItems = item
		.parent()
		.find(`${itemSelectorString}:not(.notification):not(.placeholder)`);
	return allSortableItems.index(item);
}

function getSortableContainment(el: JQuery<HTMLElement>): string {
	let containment = 'parent';

	if (el.closest('.sortable-table-container').length > 0) {
		containment = '.sortable-table-container';
	}

	return containment;
}

/**
 * Sets table column widths during sortable drag operation.
 * Prevents column width recalculation when a table row is dragged (position: absolute).
 * Should be called in the sortable 'start' callback.
 *
 * @param ui - The jQuery UI sortable ui object containing the dragged item
 */
function setTableCellWidthsOnDragStart(ui: jQueryUIInstance): void {
	const tableRowNode = ui.item[0]!;
	const closestTableRowNode = tableRowNode.parentElement?.querySelector(
		`tr:not(.notification):not(.drop-placeholder):not(.ui-sortable-helper):not([data-id="${tableRowNode.getAttribute('data-id')}"])`,
	);

	if (closestTableRowNode) {
		tableRowNode.querySelectorAll('td').forEach((cellNode: HTMLElement, index: number) => {
			if (cellNode.offsetWidth > 0) {
				const cell = closestTableRowNode.children[index] as HTMLElement | undefined;

				if (cell) {
					const width = `${cell.offsetWidth}px`;

					cellNode.style.width = width;
					cellNode.style.maxWidth = width;
				}
			}
		});
	}
}

/**
 * Clears table column widths after sortable drag operation.
 * Restores normal width behavior after drag ends.
 * Should be called in the sortable 'stop' callback.
 *
 * @param ui - The jQuery UI sortable ui object containing the dragged item
 */
function clearTableCellWidthsOnDragStop(ui: jQueryUIInstance): void {
	ui.item[0]!.querySelectorAll('td').forEach((cellNode: HTMLElement) => {
		cellNode.style.width = '';
		cellNode.style.maxWidth = '';
	});
}

/**
 * Determines whether the given HTMLElement is scrollable.
 *
 * @param node - The HTMLElement to check for scrollability.
 * @returns True if the element is scrollable vertically or horizontally, false otherwise.
 */
function isElementScrollable(node: HTMLElement): boolean {
	return node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth;
}

/**
 * Returns the focused element, also when it sits inside a shadow DOM (React features
 * from ember-integrations). document.activeElement stops at the shadow host, so walk
 * the activeElement chain through the shadow roots.
 */
function getDeepActiveElement(): Element | null {
	let node = document.activeElement;

	while (node?.shadowRoot?.activeElement) {
		node = node.shadowRoot.activeElement;
	}

	return node;
}

/**
 * Checks whether an editable element (input, textarea, contenteditable) is
 * currently focused, also inside a shadow DOM.
 */
function isEditableElementActive(): boolean {
	const node = getDeepActiveElement();

	if (!node) {
		return false;
	}

	return (
		node.matches('input, textarea, .contenteditable, .mce-edit-focus') ||
		(node as HTMLElement).isContentEditable
	);
}

export {
	centerElements,
	scaleValToAutofitJQ,
	scaleValToAutofit,
	scaleTextToAutoFit,
	scrollGuard,
	isElementOnScreen,
	nl2br,
	br2nl,
	replaceTextLinksWithHtml,
	parseMarkdownToHtml,
	getWidescreenSizeThatFits,
	getMaxSizeForRatio,
	sanitize,
	escapeHtml,
	isElementVisible,
	getPreciseWidth,
	getPreciseHeight,
	getWidthWithMargins,
	getHeightWithMargins,
	getWidthWithoutPaddings,
	getHeightWithoutPaddings,
	getChildrenHeightSum,
	stripAllHtml,
	stripHtml,
	cleanupDuplicateNodes,
	getSortableItemIndex,
	setTableCellWidthsOnDragStart,
	clearTableCellWidthsOnDragStop,
	getSortableContainment,
	isElementScrollable,
	getDeepActiveElement,
	isEditableElementActive,
};
